import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import { BackdropCircular } from 'components';
import {
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Icon,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Grow,
  makeStyles,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import {
  getStoresAPI,
  deleteStoreAPI,
  addStoreAPI,
  getCatalogStoreCodesAPI
} from 'api';
import { storeTypeNameUI } from 'helpers/storeTypeNames';


const useStyles = makeStyles(() => ({
  root: {
    marginTop: '20px',
    '& .MuiTableCell-head, & .MuiTableCell-head': {
      color: '#6a6a6a',
      fontSize: '14px',
      fontWeight: 600
    },
  }
}));

const storeValidationSchema = yup
  .object()
  .shape({
    isCreated: yup.boolean(),
    accessToken: yup.string().when('isCreated', {
      is: true,
      then: yup.string().required('Field is required'),
      otherwise: yup.string(),
    }),
    storeId: yup.number().when('isCreated', {
      is: true,
      then: yup.number().required('Field is required'),
      otherwise: yup.number(),
    }),
    storeCode: yup.string().required('Field is required'),
    storeUrl: yup.string().required('Field is required'),
    apiUrl: yup.string().required('Field is required'),
    oneloginAppId: yup.number().nullable().required('Field is required'),
    quoteProStoreId: yup.number().when(['quoteProStoreCode', 'quoteProCustomerUrl', 'checkoutUrl'], {
      is: (quoteProStoreCode, quoteProCustomerUrl, checkoutUrl) =>
        quoteProStoreCode !== undefined || quoteProCustomerUrl !== undefined || checkoutUrl !== undefined,
      then: yup.number().required('Field is required')
    }),
    quoteProStoreCode: yup.string().when(['quoteProStoreId', 'quoteProCustomerUrl', 'checkoutUrl'], {
      is: (quoteProStoreId, quoteProCustomerUrl, checkoutUrl) =>
        quoteProStoreId !== undefined || quoteProCustomerUrl !== undefined || checkoutUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
    quoteProCustomerUrl: yup.string().when(['quoteProStoreId', 'quoteProStoreCode', 'checkoutUrl'], {
      is: (quoteProStoreId, quoteProStoreCode, checkoutUrl) =>
        quoteProStoreId !== undefined || quoteProStoreCode !== undefined || checkoutUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
    checkoutUrl: yup.string().when(['quoteProStoreId', 'quoteProStoreCode', 'quoteProCustomerUrl'], {
      is: (quoteProStoreId, quoteProStoreCode, quoteProCustomerUrl) =>
        quoteProStoreId !== undefined || quoteProStoreCode !== undefined || quoteProCustomerUrl !== undefined,
      then: yup.string().required('Field is required')
    }),
  }, [
    ['quoteProStoreId', 'quoteProStoreCode'],
    ['quoteProStoreId', 'quoteProCustomerUrl'],
    ['quoteProStoreId', 'checkoutUrl'],
    ['quoteProStoreCode', 'quoteProCustomerUrl'],
    ['quoteProStoreCode', 'checkoutUrl'],
    ['quoteProCustomerUrl', 'checkoutUrl']
  ]);


const UserStores = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const [storeUrlParam, setStoreUrlParam] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { stores, storesTotalCount, updateUserProfile, catalogStoreCodes, loading } = useSelector((state: IReduxState) => state.admin);
  const {profile} = useSelector((state: IReduxState) => state.user);
  const permissions = profile?.config?.permissions || {};

  useEffect(() => {
    getCatalogStoreCodesAPI({dispatch});

    const params = new URLSearchParams({
      storeUrl: storeUrlParam,
      page: String(page),
      limit: String(limit),
    }).toString();

    getStoresAPI({dispatch, params});
  }, [
    storeUrlParam,
    page,
    limit
  ]);

  const handleSubmit = (values) => {
    for (const key in values) {
      if (typeof values[key] === 'string') {
        values[key] = values[key].trim();
      }
    }

    addStoreAPI({dispatch, store: values});
  };

  const handleStoreDelete = (url, id) => {
    deleteStoreAPI({dispatch, url, id});
  };

  const openStoreEdit = (id) => {
    history.push(`/admin/store/${id}`);
  };

  const onSearch = (searchValue) => {
    setStoreUrlParam(searchValue);
    setPage(1);
    setLimit(20);
  };

  const storesColumns = [
    {
      name: 'storeId',
      label: 'Store ID',
      options: {
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
      }
    },
    {
      name: 'storeCode',
      label: 'Store Code',
      options: {}
    },
    {
      name: 'storeUrl',
      label: 'Store URL',
      options: {
        setCellHeaderProps: () => ({ style: { width: '400px' } }),
      }
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const store = stores[dataIndex];

          return (
            <div className="flex items-center">
              {store.createdAt ? store.createdAt.slice(0, 10) : 'Just now'}
            </div>
          );
        },
      }
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {apiUrl, _id} = stores[dataIndex];
          return (
            <div className="flex items-center">
              {permissions?.adminStoresRemove &&
              <IconButton
                className="mr-2"
                title="Delete"
                onClick={() => handleStoreDelete(apiUrl, _id)}
              >
                <Icon className="text-gray">delete</Icon>
              </IconButton>}
              {permissions?.adminStoresEdit &&
              <IconButton
                className="mr-2"
                title="Edit"
                onClick={() => openStoreEdit(_id)}
              >
                <Icon className="text-gray">edit</Icon>
              </IconButton>}
            </div>
          );
        },
      }
    }
  ];

  const options = {
    filterType: 'textField',
    selectableRows: 'none',
    rowsPerPageOptions: [10, 20, 50, 100],
    serverSide: true,
    search: true,
    customSearchRender: (
      searchText,
      handleSearch,
      hideSearch
    ) => {
      return (
        <Grow appear in timeout={300}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={searchValue}
            onChange={({ target: { value } }) => {
              setSearchValue(value);
            }}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearch(searchValue);
              }
            }}
            placeholder="Enter store URL"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => {
                    setSearchValue('');
                    onSearch('');
                    hideSearch();
                  }}
                >
                  <Icon fontSize="small">
                    clear
                  </Icon>
                </IconButton>
              ),
            }}
          />
        </Grow>
      );
    },
    filter: false,
    sort: false,
    download: true,
    print: true,
    viewColumns: true,
    rowsPerPage: limit,
    page: page - 1,
    count: storesTotalCount,
    onChangePage: (page) => setPage(page + 1),
    onChangeRowsPerPage: (newPageSize) => {
      setLimit(newPageSize);
      setPage(1);
    },
    setTableProps: () => ({
      headRow: {
        fontWeight: 'bold',
      },
    }),
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25 my-10 ml-10 pr-10">
      {permissions?.adminStoresAdd &&
      <div className="mb-10" >
        <Typography variant="h3" className="mb-8">Add New Store</Typography>
        <Formik
          initialValues={{
            storeId: '',
            storeCode: '',
            accessToken: '',
            storeUrl: '',
            apiUrl: '',
            isCreated: true,
            isLive: false,
            isOldMigrated: false,
            storeType: 'regular',
            catalogStoreCode: 'all',
            quoteProStoreId: '',
            quoteProStoreCode: '',
            quoteProCustomerUrl: '',
            checkoutUrl: '',
            oneloginAppId: null,
            alleInsightUrl: null,
            alleInsightApiKey: null,
            rpaUrl: null,
            rpaApiKey: null,
          }}
          validationSchema={storeValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form noValidate autoComplete="off" className="w-full">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2}>
                  <TextField
                    variant="outlined"
                    type="number"
                    name="storeId"
                    value={values.storeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.storeId && Boolean(errors.storeId)}
                    helperText={touched.storeId && errors.storeId}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Store ID
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth>
                    <InputLabel id="store-type-label">Store Type:</InputLabel>
                    <Select
                      labelId="store-type-label"
                      name="storeType"
                      value={values.storeType}
                      variant="outlined"
                      onChange={handleChange}
                    >
                      {Object.keys(storeTypeNameUI).map((type) => (
                        <MenuItem
                          key={`storeType-${type}`}
                          value={type}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth>
                    <InputLabel>Catalog store code:</InputLabel>
                    <Select
                      value={values.catalogStoreCode}
                      variant="outlined"
                      name="catalogStoreCode"
                      onChange={handleChange}
                    >
                      {catalogStoreCodes.map((storeCode) => (
                        <MenuItem key={storeCode} value={storeCode} >{storeCode}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={handleChange}
                        checked={values.isCreated}
                        color="primary"
                        name="isCreated"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        className="ml-2"
                      />}
                    label="Created"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={handleChange}
                        checked={values.isLive}
                        color="primary"
                        name="isLive"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        className="ml-2"
                      />}
                    label="Is Live"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={handleChange}
                        checked={values.isOldMigrated}
                        color="primary"
                        name="isOldMigrated"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        className="ml-2"
                      />}
                    label="Old migrated"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="text"
                    fullWidth
                    name="storeCode"
                    value={values.storeCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.storeCode && Boolean(errors.storeCode)}
                    helperText={touched.storeCode && errors.storeCode}
                    label={
                      <div className="flex items-center">
                        Store Code
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="text"
                    name="storeUrl"
                    fullWidth
                    value={values.storeUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.storeUrl && Boolean(errors.storeUrl)}
                    helperText={touched.storeUrl && errors.storeUrl}
                    label={
                      <div className="flex items-center">
                        Store URL
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="text"
                    name="apiUrl"
                    fullWidth
                    value={values.apiUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.apiUrl && Boolean(errors.apiUrl)}
                    helperText={touched.apiUrl && errors.apiUrl}
                    label={
                      <div className="flex items-center">
                        apiURL
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="text"
                    name="accessToken"
                    fullWidth
                    value={values.accessToken}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.accessToken && Boolean(errors.accessToken)}
                    helperText={touched.accessToken && errors.accessToken}
                    label={
                      <div className="flex items-center">
                        accessToken
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="number"
                    name="quoteProStoreId"
                    fullWidth
                    value={values.quoteProStoreId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.quoteProStoreId && Boolean(errors.quoteProStoreId)}
                    helperText={touched.quoteProStoreId && errors.quoteProStoreId}
                    label={
                      <div className="flex items-center">
                        Quote Pro Store Id
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="text"
                    fullWidth
                    name="quoteProStoreCode"
                    value={values.quoteProStoreCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.quoteProStoreCode && Boolean(errors.quoteProStoreCode)}
                    helperText={touched.quoteProStoreCode && errors.quoteProStoreCode}
                    label={
                      <div className="flex items-center">
                        Quote Pro Store Code
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    name="quoteProCustomerUrl"
                    value={values.quoteProCustomerUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.quoteProCustomerUrl && Boolean(errors.quoteProCustomerUrl)}
                    helperText={touched.quoteProCustomerUrl && errors.quoteProCustomerUrl}
                    label={
                      <div className="flex items-center">
                        QuotePro Customer Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    name="checkoutUrl"
                    value={values.checkoutUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.checkoutUrl && Boolean(errors.checkoutUrl)}
                    helperText={touched.checkoutUrl && errors.checkoutUrl}
                    label={
                      <div className="flex items-center">
                        Checkout Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    type="number"
                    name="oneloginAppId"
                    value={values.oneloginAppId}
                    onChange={handleChange}
                    error={touched.oneloginAppId && Boolean(errors.oneloginAppId)}
                    helperText={touched.oneloginAppId && errors.oneloginAppId}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Onelogin App Id
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="alleInsightUrl"
                    value={values.alleInsightUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Alle Service Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="alleInsightApiKey"
                    value={values.alleInsightApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Alle Api Key
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="rpaUrl"
                    value={values.rpaUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        RPA Service Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="rpaApiKey"
                    value={values.rpaApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        RPA Api Key
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="touchmdServiceUrl"
                    value={values.touchmdServiceUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        TOUCHMD Service Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="touchmdServiceApiKey"
                    value={values.touchmdServiceApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        TOUCHMD Service Api Key
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="aspireServiceUrl"
                    value={values.aspireServiceUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        ASPIRE Service Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="aspireServiceApiKey"
                    value={values.aspireServiceApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        ASPIRE Api Key
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="modmedServiceUrl"
                    value={values.modmedServiceUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        MODMED Service Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="modmedServiceApiKey"
                    value={values.modmedServiceApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        MODMED Api Key
                      </div>
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="ghlLocationId"
                    value={values.ghlLocationId}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        GHL Location Id
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="ghlApiKey"
                    value={values.ghlApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        GHL Api Key
                      </div>
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="aestheticKpiWarehouseUrl"
                    value={values.aestheticKpiWarehouseUrl}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Aesthetic Kpi Warehouse Url
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="aestheticKpiWarehouseApiKey"
                    value={values.aestheticKpiWarehouseApiKey}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Aesthetic Kpi Warehouse Api Key
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="nextechUsername"
                    value={values.nextechUsername}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Nextech Username
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="nextechPassword"
                    value={values.nextechPassword}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Nextech Password
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="nextechClientId"
                    value={values.nextechClientId}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Nextech Client Id
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="outlined"
                    name="nextechPracticeId"
                    value={values.nextechPracticeId}
                    onChange={handleChange}
                    fullWidth
                    label={
                      <div className="flex items-center">
                        Nextech Practice Id
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    className="w-full text-18 uppercase"
                    onClick={() => handleSubmit(values)}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </form>
          )
          }
        </Formik>
      </div>}
      <Divider />
      <MUIDataTable
        className={classes.root}
        title={'Stores'}
        data={stores}
        columns={storesColumns}
        options={options}
      />
      <BackdropCircular loading={updateUserProfile || loading} />
    </div>
  );
};

export default UserStores;
